import { supabase } from '../lib/supabaseClient';

export type TransferBroadcastInput = {
  eventLineId: string;
  candidateLocationIds: string[];
  needBy?: string | null;
  expectedReturn?: string | null;
  // Optional: per-location approver profile IDs (inventory managers) to notify/assign
  // [{ locationId, approverProfileIds }]
  targets?: Array<{ locationId: string; approverProfileIds: string[] }>;
};

export async function requestTransferBroadcast(
  input: TransferBroadcastInput,
): Promise<unknown> {
  if (!input.eventLineId) {
    throw new Error('eventLineId is required');
  }

  const args: Record<string, any> = {
    p_event_line_id: input.eventLineId,
    p_candidate_location_ids: input.candidateLocationIds,
    p_need_by: input.needBy ?? null,
    p_expected_return: input.expectedReturn ?? null,
  };
  
  if (input.targets && input.targets.length > 0) {
    args.p_target_approver_profile_ids_by_location = input.targets.map(t => ({
      location_id: t.locationId,
      approver_profile_ids: t.approverProfileIds,
    }));
  }
  
  const { data, error } = await supabase.rpc('erp_request_transfer_broadcast', args);
  if (error) throw error;
  return data;
}

// Ship, receive and return legs go through the Warehouse actions (data/transferRecord.ts); signed-in
// users can no longer call erp_ship_transfer, erp_receive_transfer or erp_create_return_transfer.

export async function acceptReturnTransferOffer(offerId: string): Promise<unknown> {
  const { data, error } = await supabase.rpc('erp_accept_return_transfer_offer', {
    p_offer_id: offerId,
  });
  if (error) throw error;
  return data;
}

export async function requestPermanentTransfer(params: {
  transferLineId: string;
  assetId?: string;
  qty?: number;
  reason?: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc('erp_request_permanent_transfer', {
    p_transfer_line_id: params.transferLineId,
    p_asset_id: params.assetId ?? null,
    p_qty: params.qty ?? 1,
    p_reason: params.reason ?? null,
  });
  if (error) throw error;
  return data as string;
}

export async function decidePermanentTransfer(params: {
  requestId: string;
  decision: 'approved' | 'rejected';
  note?: string;
}): Promise<void> {
  const { error } = await supabase.rpc('erp_decide_permanent_transfer', {
    p_request_id: params.requestId,
    p_decision: params.decision,
    p_note: params.note ?? null,
  });
  if (error) throw error;
}

export async function updateTransferBroadcast(
  broadcastId: string,
  updates: {
    qty?: number;
    neededByAt?: string;
    expectedReturnAt?: string;
    candidateLocationIds?: string[];
  },
): Promise<unknown> {
  const { data, error } = await supabase.rpc('erp_update_transfer_broadcast', {
    p_broadcast_id: broadcastId,
    p_qty: updates.qty ?? null,
    p_needed_by_at: updates.neededByAt ?? null,
    p_expected_return_at: updates.expectedReturnAt ?? null,
    p_candidate_location_ids: updates.candidateLocationIds ?? null,
  });
  if (error) throw error;
  return data;
}

/**
 * Accept a transfer broadcast (request).
 * Called by the CANDIDATE location that received the request and wants to fulfill it.
 * Creates a transfer order from the accepting location to the requesting location.
 * Cancels other open broadcasts for the same shortage.
 */
export async function acceptTransferBroadcast(
  broadcastId: string,
): Promise<{ success: boolean; transfer_order_id?: string }> {
  const { data, error } = await supabase.rpc('erp_accept_transfer_broadcast', {
    p_broadcast_id: broadcastId,
  });
  if (error) throw error;
  if (!data) return { success: false };
  if (Array.isArray(data)) {
    return (data[0] as { success: boolean; transfer_order_id?: string }) ?? { success: false };
  }
  return data as { success: boolean; transfer_order_id?: string };
}

/**
 * Reject a transfer broadcast (request).
 * Called by the CANDIDATE location that received the request but cannot/will not fulfill it.
 */
export async function rejectTransferBroadcast(
  broadcastId: string,
  note?: string,
): Promise<{ success: boolean }> {
  const { data, error } = await supabase.rpc('erp_reject_transfer_broadcast', {
    p_broadcast_id: broadcastId,
    p_note: note ?? null,
  });
  if (error) throw error;
  if (!data) return { success: false };
  if (Array.isArray(data)) {
    return (data[0] as { success: boolean }) ?? { success: false };
  }
  return data as { success: boolean };
}
